import { FC, useCallback, useMemo, useState } from 'react';
import { MenuItem, TextField } from '@mui/material';
import { api } from 'services/api';
import { TFilters } from 'redux/types';
import { TPgPaymentListItem } from 'services/api/paymentGateway/types';
import { DATE_FILTER } from 'ui/EasTable/constants';
import {
	ESortDirection,
	ETableStructureAlign,
	ETableStructureType,
	TTableStructure,
} from 'ui/EasTable/types';
import { PG_METHOD_TYPE_LABELS, PG_METHOD_TYPES } from '../constants';
import { PgList } from '../common/PgList';
import { DETAILS_BUTTON, pgActionsColumn, pgColumn, statusColumn } from '../common/tableHelpers';
import { formatMoney } from '../utils';
import { usePaymentViewer } from './PaymentViewer';

type TRow = TPgPaymentListItem;
const col = (
	name: string,
	selector: keyof TRow & string,
	type: ETableStructureType,
	extra: Partial<TTableStructure<keyof TRow, TRow>> = {},
) => pgColumn<TRow>(name, selector, type, extra);

const STATUSES = [
	'created',
	'awaiting_customer',
	'processing',
	'pending',
	'succeeded',
	'failed',
	'cancelled',
	'unknown',
];
const PURPOSES = ['invoice', 'eu_ddp_prepayment', 'card_verification'];
const TRIGGERS = ['link', 'dashboard_manual', 'auto', 'after_binding', 'eu_ddp', 'admin', 'setup'];
const EAS_SYNC = ['not_required', 'pending', 'done', 'failed', 'conflict'];

const STRUCTURE: TTableStructure<keyof TRow, TRow>[] = [
	col('Created', 'createdAt', ETableStructureType.DATE_TIME, {
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
	}),
	col('Status', 'status', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn(STATUSES),
	}),
	col('Purpose', 'purpose', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn(PURPOSES),
	}),
	col('Trigger', 'trigger', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn(TRIGGERS),
	}),
	col('Client', 'clientNumber', ETableStructureType.STRING, {
		sortable: true,
		searchable: false,
		filterable: true,
	}),
	col('Invoice', 'invoiceNumber', ETableStructureType.STRING, {
		sortable: true,
		filterable: true,
		value: (item) => (item.invoiceNumber === null ? '' : String(item.invoiceNumber)),
	}),
	col('Service', 'serviceCode', ETableStructureType.STRING),
	col('Method', 'methodType', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn(PG_METHOD_TYPES, PG_METHOD_TYPE_LABELS),
		tooltip: (item) => item.label ?? '',
	}),
	col('Amount', 'baseAmount', ETableStructureType.STRING, {
		align: ETableStructureAlign.RIGHT,
		value: (item) => formatMoney(item.baseAmount, item.currency),
	}),
	col('Fee', 'fee', ETableStructureType.STRING, {
		align: ETableStructureAlign.RIGHT,
		value: (item) => formatMoney(item.fee, item.currency),
	}),
	col('Total', 'total', ETableStructureType.STRING, {
		align: ETableStructureAlign.RIGHT,
		value: (item) => formatMoney(item.total, item.currency),
	}),
	col('Refunded', 'refundedAmount', ETableStructureType.STRING, {
		align: ETableStructureAlign.RIGHT,
		value: (item) =>
			item.refundedAmount > 0
				? formatMoney(item.refundedAmount, item.chargedCurrency ?? item.currency)
				: '',
	}),
	col('EAS sync', 'easSyncStatus', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn(EAS_SYNC),
	}),
	col('Failure', 'failureMessage', ETableStructureType.STRING, {
		tooltip: (item) => item.failureMessage ?? '',
		value: (item) => item.failureCode ?? '',
	}),
	pgActionsColumn<TRow>([DETAILS_BUTTON]),
];

type TYesNoAny = '' | 'true' | 'false';

type TPaymentsListProps = {
	reloadKey: number;
};

/** Payments with the standard filters plus "settled" and "has dispute" switches (03 §4.8). */
export const PaymentsList: FC<TPaymentsListProps> = ({ reloadKey }) => {
	const { openPayment, changeCounter } = usePaymentViewer();
	const [settled, setSettled] = useState<TYesNoAny>('');
	const [dispute, setDispute] = useState<TYesNoAny>('');

	const extraFilters = useMemo<TFilters>(() => {
		const filters: TFilters = [];
		if (settled)
			filters.push({ fieldName: 'settled', name: 'settled', value: settled, operator: '=' });
		if (dispute) {
			filters.push({ fieldName: 'hasDispute', name: 'hasDispute', value: dispute, operator: '=' });
		}
		return filters;
	}, [settled, dispute]);

	const handleAction = useCallback(
		(row: TRow, action: string) => {
			if (action === DETAILS_BUTTON.name) openPayment(row.transactionId);
		},
		[openPayment],
	);

	const yesNo = (label: string, value: TYesNoAny, onChange: (v: TYesNoAny) => void) => (
		<TextField
			select
			size="small"
			label={label}
			value={value}
			onChange={(e) => onChange(e.target.value as TYesNoAny)}
			sx={{ minWidth: 120, backgroundColor: 'background.paper' }}
		>
			<MenuItem value="">Any</MenuItem>
			<MenuItem value="true">Yes</MenuItem>
			<MenuItem value="false">No</MenuItem>
		</TextField>
	);

	return (
		<PgList<TRow>
			title="Payments"
			structure={STRUCTURE}
			fetcher={api.paymentGateway.getPayments}
			onAction={handleAction}
			extraFilters={extraFilters}
			defaultOrder={{ fieldName: 'createdAt', direction: ESortDirection.DESC }}
			reloadKey={reloadKey + changeCounter}
			errorText="Failed to load payments"
			headerContent={
				<>
					{yesNo('Settled', settled, setSettled)}
					{yesNo('Has dispute', dispute, setDispute)}
				</>
			}
		/>
	);
};
